// WA4 i18n driver for the Google Website Translate widget (loaded in public/index.html).
//
// The widget translates the whole rendered DOM (en → hi/or) and reads the `googtrans` cookie on init,
// so a saved language re-applies on every load. We expose a tiny API the LanguageSwitcher calls:
//   - getActiveLang()      → the current language code from the cookie ("en" default)
//   - applyTranslate(lang) → persist the choice + (re)translate the page
//
// We reload on change: it's the only way to translate lazily-loaded routes consistently AND to cleanly
// restore the original English (Google's in-place "show original" is unreliable). A language switch is a
// deliberate, rare action, so the reload cost is acceptable.
// Codes are Google Translate's own ISO 639-1 identifiers, and must stay in sync with the widget's
// own `includedLanguages` allow-list in index.html — Google silently refuses to translate to a
// code that isn't in BOTH places, so adding a language here without updating index.html leaves
// the switcher offering a language the widget won't actually apply.
// `english` is the display-only English name shown as a subtitle under the native label in the
// LanguageSwitcher's premium two-line rows — a reader who can't parse the native script yet still
// recognizes which language a row is.
export const TRANSLATE_LANGS = [
  { code: "en", label: "English", english: "English" },
  { code: "hi", label: "हिन्दी", english: "Hindi" },
  { code: "or", label: "ଓଡ଼ିଆ", english: "Odia" },
  { code: "te", label: "తెలుగు", english: "Telugu" },
  { code: "kn", label: "ಕನ್ನಡ", english: "Kannada" },
  { code: "ta", label: "தமிழ்", english: "Tamil" },
  { code: "bn", label: "বাংলা", english: "Bengali" },
  { code: "ml", label: "മലയാളം", english: "Malayalam" },
  { code: "mr", label: "मराठी", english: "Marathi" },
];
const VALID = TRANSLATE_LANGS.map((l) => l.code);
const COOKIE = "googtrans";

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name, value) {
  // googtrans must be readable by the widget across path + host (and the registrable domain for
  // subdomain tenants), so set it on every relevant scope. 1-year persistence.
  const maxAge = 60 * 60 * 24 * 365;
  const host = window.location.hostname;
  const base = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
  document.cookie = base;
  if (host && host !== "localhost") {
    document.cookie = `${base};domain=${host}`;
    const parts = host.split(".");
    if (parts.length > 2) document.cookie = `${base};domain=.${parts.slice(-2).join(".")}`;
  }
}

// Perf: the widget script is no longer loaded eagerly by index.html — it self-loads only when a
// saved non-English cookie must re-apply. This warms it up on the first switcher interaction so a
// first-time language pick has the widget ready (idempotent; the bootstrap lives in index.html).
export function ensureWidgetLoaded() {
  try { window.__vpLoadGoogleTranslate && window.__vpLoadGoogleTranslate(); } catch { /* ignore */ }
}

// "/en/hi" → "hi"; "/en/en" or missing → "en".
export function getActiveLang() {
  const c = readCookie(COOKIE);
  if (!c) return "en";
  const to = c.split("/")[2];
  return VALID.includes(to) ? to : "en";
}

// Persist the chosen language and (re)translate the page.
// Returns true when a reload was triggered, false when the choice is already active
// (no wasteful reload). Throws "cookie-write-failed" if cookies are blocked (the widget
// reads its language from the cookie, so without it the reload would silently do nothing).
export function applyTranslate(lang) {
  if (!VALID.includes(lang)) lang = "en";
  if (lang === getActiveLang()) return false;       // already active — don't reload for nothing
  writeCookie(COOKIE, `/en/${lang}`);
  if (getActiveLang() !== lang) {                   // cookie didn't persist (blocked/private mode)
    const err = new Error("cookie-write-failed");
    err.code = "cookie-write-failed";
    throw err;
  }
  // Reload so the widget re-applies from the cookie across the whole app (incl. lazy routes).
  window.location.reload();
  return true;
}

// True once Google has actually rewritten the DOM (it adds `translated-ltr` to <html>).
export function isTranslated() {
  const c = document.documentElement.classList;
  return c.contains("translated-ltr") || c.contains("translated-rtl");
}

// After a reload into a non-English language, confirm the widget actually applied it.
// If Google's script is blocked/unreachable the page silently stays English — we call
// onFail() once (at most once per browser session) so the UI can tell the user instead
// of failing silently. No-op when the active language is English. Returns a cleanup fn.
export function verifyTranslation(onFail, { timeoutMs = 9000, intervalMs = 400 } = {}) {
  const noop = () => {};
  if (getActiveLang() === "en") return noop;
  let warned = false;
  try { warned = sessionStorage.getItem("gt-warned") === "1"; } catch { /* storage blocked */ }
  if (warned) return noop;

  let tries = Math.ceil(timeoutMs / intervalMs);
  let stopped = false;
  const timer = setInterval(() => {
    if (stopped) return;
    if (isTranslated()) { stopped = true; clearInterval(timer); return; }
    if (--tries <= 0) {
      stopped = true;
      clearInterval(timer);
      try { sessionStorage.setItem("gt-warned", "1"); } catch { /* ignore */ }
      try { onFail && onFail(); } catch { /* ignore */ }
    }
  }, intervalMs);
  return () => { stopped = true; clearInterval(timer); };
}
